import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Grid, Chip, IconButton, Tooltip, Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

const API = '/api/contabilidad';
function fmt(v) { return parseFloat(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

const FORM_INIT = { tipo: 'RETENCION', proveedor_ruc: '', proveedor_nombre: '', tipo_documento: '01', serie: '', numero: '', fecha_emision: new Date().toISOString().substring(0,10), base_imponible: '', porcentaje: '3', importe: '', cuenta_id: '' };

const PORCENTAJES = {
  RETENCION: [{ v: '3', l: '3% — Retención de IGV' }],
  DETRACCION: [
    { v: '12', l: '12% — Servicios generales' },
    { v: '10', l: '10% — Transporte de bienes' },
    { v: '9',  l: '9% — Arrendamiento' },
    { v: '8',  l: '8% — Servicios empresariales' },
    { v: '4',  l: '4% — Transporte público de pasajeros' },
    { v: '15', l: '15% — Contrato de construcción' },
    { v: '12', l: '12% — Intermediación laboral' },
  ]
};

export default function Retenciones() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [periodos, setPeriodos] = useState([]);
  const [periodoSel, setPeriodoSel] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cuentas, setCuentas] = useState([]);

  // CRUD
  const [dlgForm, setDlgForm] = useState(false);
  const [form, setForm] = useState(FORM_INIT);
  const [saving, setSaving] = useState(false);

  // Depositar
  const [dlgDep, setDlgDep] = useState(null);
  const [depForm, setDepForm] = useState({ num_constancia: '', fecha_deposito: new Date().toISOString().substring(0,10), cuenta_id: '' });
  const [depositando, setDepositando] = useState(false);

  // Resumen
  const [resumen, setResumen] = useState([]);

  useEffect(() => { axios.get(`${API}/empresas`).then(r => setEmpresas(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!empresaId) return;
    axios.get(`${API}/periodos?empresa_id=${empresaId}`).then(r => {
      setPeriodos(r.data);
      const abiertos = r.data.filter(p => p.estado === 'ABIERTO').sort((a,b) => b.periodo.localeCompare(a.periodo));
      if (abiertos.length > 0) setPeriodoSel(abiertos[0].periodo);
    }).catch(() => {});
    axios.get(`${API}/plan?empresa_id=${empresaId}`).then(r => {
      setCuentas(r.data.filter(c => c.codigo?.startsWith('40')));
    }).catch(() => {});
  }, [empresaId]);

  useEffect(() => { if (empresaId && periodoSel) { cargarRegistros(); cargarResumen(); } }, [empresaId, periodoSel, tipoFiltro, estadoFiltro]);

  const cargarRegistros = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ empresa_id: empresaId });
      if (periodoSel) params.append('periodo', periodoSel);
      if (tipoFiltro) params.append('tipo', tipoFiltro);
      if (estadoFiltro) params.append('estado', estadoFiltro);
      const r = await axios.get(`${API}/retenciones?${params}`);
      setRegistros(r.data);
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
    setLoading(false);
  }, [empresaId, periodoSel, tipoFiltro, estadoFiltro]);

  const cargarResumen = async () => {
    if (!empresaId || !periodoSel) return;
    try {
      const r = await axios.get(`${API}/retenciones/resumen?empresa_id=${empresaId}&periodo=${periodoSel}`);
      setResumen(r.data);
    } catch (_) {}
  };

  function setF(k, v) {
    setForm(f => {
      const nuevo = { ...f, [k]: v };
      if (k === 'base_imponible' || k === 'porcentaje') {
        const base = parseFloat(k === 'base_imponible' ? v : nuevo.base_imponible) || 0;
        const pct  = parseFloat(k === 'porcentaje' ? v : nuevo.porcentaje) || 0;
        nuevo.importe = (base * pct / 100).toFixed(2);
      }
      if (k === 'tipo') nuevo.porcentaje = nuevo.tipo === 'RETENCION' ? '3' : '12';
      return nuevo;
    });
  }

  async function guardar() {
    if (!form.proveedor_ruc || !form.base_imponible || !form.importe) return setError('Completa los campos requeridos');
    setSaving(true);
    try {
      await axios.post(`${API}/retenciones`, { ...form, empresa_id: empresaId, periodo: periodoSel });
      setSuccess('Registro creado'); setDlgForm(false); cargarRegistros(); cargarResumen();
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
    setSaving(false);
  }

  async function eliminar(r) {
    if (!window.confirm(`¿Eliminar retención/detracción de ${r.proveedor_nombre || r.proveedor_ruc}?`)) return;
    try { await axios.delete(`${API}/retenciones/${r.id}`); setSuccess('Eliminado'); cargarRegistros(); cargarResumen(); }
    catch (e) { setError(e.response?.data?.error || 'Error'); }
  }

  async function depositar() {
    if (!depForm.num_constancia || !depForm.fecha_deposito) return setError('Constancia y fecha requeridas');
    setDepositando(true);
    try {
      await axios.put(`${API}/retenciones/${dlgDep.id}/depositar`, { ...depForm, cuenta_id: depForm.cuenta_id || null });
      setSuccess('Registrado como DEPOSITADO'); setDlgDep(null); cargarRegistros(); cargarResumen();
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
    setDepositando(false);
  }

  const estadoColor = (e) => e === 'DEPOSITADO' ? 'success' : e === 'DECLARADO' ? 'primary' : 'warning';
  const tipoColor   = (t) => t === 'RETENCION' ? 'error' : 'info';

  const totalPendiente = registros.filter(r => r.estado === 'PENDIENTE').reduce((s, r) => s + parseFloat(r.importe || 0), 0);
  const totalDepositado = registros.filter(r => r.estado === 'DEPOSITADO').reduce((s, r) => s + parseFloat(r.importe || 0), 0);

  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <ReceiptLongIcon color="primary" />
        <Typography variant="h5" fontWeight="bold">Retenciones y Detracciones</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Empresa" value={empresaId} onChange={e => setEmpresaId(e.target.value)} sx={{ minWidth: '24em' }}>
              {empresas.map(e => <MenuItem key={e.id} value={e.id}>{e.razon_social}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Período" value={periodoSel} onChange={e => setPeriodoSel(e.target.value)} disabled={!empresaId} sx={{ minWidth: '18em' }}>
              {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{p.nombre || p.periodo}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Tipo" value={tipoFiltro} onChange={e => setTipoFiltro(e.target.value)} sx={{ minWidth: '16em' }}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="RETENCION">Retención IGV (3%)</MenuItem>
              <MenuItem value="DETRACCION">Detracción SPOT</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Estado" value={estadoFiltro} onChange={e => setEstadoFiltro(e.target.value)} sx={{ minWidth: '14em' }}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="PENDIENTE">Pendiente</MenuItem>
              <MenuItem value="DEPOSITADO">Depositado</MenuItem>
              <MenuItem value="DECLARADO">Declarado</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm="auto">
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm({ ...FORM_INIT, fecha_emision: new Date().toISOString().substring(0,10) }); setDlgForm(true); }} disabled={!empresaId || !periodoSel}>
                Nuevo
              </Button>
              <IconButton onClick={cargarRegistros} disabled={loading}><RefreshIcon /></IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* KPIs */}
      {registros.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2, borderColor: 'warning.main' }}>
              <Typography variant="caption" color="warning.dark">Pendiente de depositar</Typography>
              <Typography fontWeight="bold" color="warning.dark">S/ {fmt(totalPendiente)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2, borderColor: 'success.main' }}>
              <Typography variant="caption" color="success.dark">Depositado</Typography>
              <Typography fontWeight="bold" color="success.dark">S/ {fmt(totalDepositado)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">Total Retenciones</Typography>
              <Typography fontWeight="bold">{registros.filter(r => r.tipo === 'RETENCION').length} registros</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">Total Detracciones</Typography>
              <Typography fontWeight="bold">{registros.filter(r => r.tipo === 'DETRACCION').length} registros</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {loading ? <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} /> : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                {['Tipo','Proveedor','RUC','F. Emisión','Comprobante','Base Imponible','%','Importe','Estado','Constancia','F. Depósito','Acc.'].map(h => (
                  <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {registros.length === 0 ? (
                <TableRow><TableCell colSpan={12} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay registros para los filtros seleccionados</TableCell></TableRow>
              ) : registros.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell><Chip label={r.tipo === 'RETENCION' ? 'RET' : 'DET'} size="small" color={tipoColor(r.tipo)} /></TableCell>
                  <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.proveedor_nombre || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.proveedor_ruc}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>{r.fecha_emision?.substring(0,10)}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{r.tipo_documento} {r.serie}-{r.numero}</TableCell>
                  <TableCell align="right">S/ {fmt(r.base_imponible)}</TableCell>
                  <TableCell align="center">{r.porcentaje}%</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>S/ {fmt(r.importe)}</TableCell>
                  <TableCell><Chip label={r.estado} size="small" color={estadoColor(r.estado)} /></TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{r.num_constancia || '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{r.fecha_deposito?.substring(0,10) || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {r.estado === 'PENDIENTE' && (<>
                      <Tooltip title="Registrar depósito">
                        <IconButton size="small" color="success" onClick={() => { setDlgDep(r); setDepForm({ num_constancia: '', fecha_deposito: new Date().toISOString().substring(0,10), cuenta_id: '' }); }}>
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" color="error" onClick={() => eliminar(r)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </>)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog nuevo registro */}
      <Dialog open={dlgForm} onClose={() => setDlgForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva Retención / Detracción</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Tipo" value={form.tipo} onChange={e => setF('tipo', e.target.value)}>
                <MenuItem value="RETENCION">Retención de IGV (3%)</MenuItem>
                <MenuItem value="DETRACCION">Detracción SPOT</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={4}><TextField fullWidth size="small" label="RUC Proveedor *" value={form.proveedor_ruc} onChange={e => setF('proveedor_ruc', e.target.value)} inputProps={{ maxLength: 20 }} /></Grid>
            <Grid item xs={8}><TextField fullWidth size="small" label="Razón Social" value={form.proveedor_nombre} onChange={e => setF('proveedor_nombre', e.target.value)} /></Grid>
            <Grid item xs={12}><Divider><Typography variant="caption">Comprobante de referencia</Typography></Divider></Grid>
            <Grid item xs={3}>
              <TextField select fullWidth size="small" label="Tipo Doc." value={form.tipo_documento} onChange={e => setF('tipo_documento', e.target.value)}>
                <MenuItem value="01">Factura</MenuItem>
                <MenuItem value="02">RHE</MenuItem>
                <MenuItem value="03">Boleta</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={3}><TextField fullWidth size="small" label="Serie" value={form.serie} onChange={e => setF('serie', e.target.value)} /></Grid>
            <Grid item xs={3}><TextField fullWidth size="small" label="Número" value={form.numero} onChange={e => setF('numero', e.target.value)} /></Grid>
            <Grid item xs={3}><TextField fullWidth size="small" label="Fecha" type="date" InputLabelProps={{ shrink: true }} value={form.fecha_emision} onChange={e => setF('fecha_emision', e.target.value)} /></Grid>
            <Grid item xs={12}><Divider><Typography variant="caption">Montos</Typography></Divider></Grid>
            <Grid item xs={4}><TextField fullWidth size="small" label="Base Imponible S/ *" type="number" value={form.base_imponible} onChange={e => setF('base_imponible', e.target.value)} inputProps={{ step: '0.01' }} /></Grid>
            <Grid item xs={4}>
              <TextField select fullWidth size="small" label="Porcentaje *" value={form.porcentaje} onChange={e => setF('porcentaje', e.target.value)}>
                {(PORCENTAJES[form.tipo] || []).map(p => <MenuItem key={p.v} value={p.v}>{p.l}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={4}><TextField fullWidth size="small" label="Importe S/ *" type="number" value={form.importe} onChange={e => setF('importe', e.target.value)} inputProps={{ step: '0.01' }} /></Grid>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Cuenta contable 40x" value={form.cuenta_id} onChange={e => setF('cuenta_id', e.target.value)}>
                <MenuItem value="">Sin asignar</MenuItem>
                {cuentas.map(c => <MenuItem key={c.id} value={c.id}>{c.codigo} — {c.descripcion}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlgForm(false)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Registrar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog depositar */}
      <Dialog open={!!dlgDep} onClose={() => setDlgDep(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Registrar Depósito</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {dlgDep?.tipo} de <strong>{dlgDep?.proveedor_nombre || dlgDep?.proveedor_ruc}</strong> — S/ {fmt(dlgDep?.importe)}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField fullWidth size="small" label="N° Constancia de Depósito *" value={depForm.num_constancia} onChange={e => setDepForm(f => ({ ...f, num_constancia: e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Fecha Depósito *" type="date" InputLabelProps={{ shrink: true }} value={depForm.fecha_deposito} onChange={e => setDepForm(f => ({ ...f, fecha_deposito: e.target.value }))} /></Grid>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Cuenta contable" value={depForm.cuenta_id} onChange={e => setDepForm(f => ({ ...f, cuenta_id: e.target.value }))}>
                <MenuItem value="">Sin asignar</MenuItem>
                {cuentas.map(c => <MenuItem key={c.id} value={c.id}>{c.codigo} — {c.descripcion}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlgDep(null)}>Cancelar</Button>
          <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={depositar} disabled={depositando}>
            {depositando ? <CircularProgress size={20} color="inherit" /> : 'Confirmar Depósito'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

